import React, { useEffect, useState } from "react";
import "./App.css";

const phrases = ["AI", "Vision", "Voice", "Agents"];

const navItems = [
  { label: "AI", href: "#ai" },
  { label: "Solutions", href: "#solutions" },
  { label: "Industries", href: "#industries" },
  { label: "AI Lab", href: "#lab" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

const capabilityCards = [
  {
    title: "AI Agents",
    text: "Autonomous assistants that reason, retrieve context, trigger workflows, and act across your business systems.",
  },
  {
    title: "Vision AI",
    text: "Computer vision pipelines that detect, classify, and interpret image and video data to drive operational decisions.",
  },
  {
    title: "Voice AI",
    text: "Speech-to-text, reasoning, and text-to-speech systems for booking, support, and enterprise conversations.",
  },
  {
    title: "Generative AI",
    text: "RAG-powered knowledge systems and copilots for employees, customers, and internal operations.",
  },
  {
    title: "Machine Learning",
    text: "Predictive models and analytics designed for forecasting, risk detection, and decision support.",
  },
  {
    title: "Enterprise Automation",
    text: "Integrations, APIs, and workflow automation that turn AI into measurable business outcomes.",
  },
];

const products = [
  {
    name: "24Streets Hospital AI",
    problem: "Hospitals need faster patient engagement, better information access, and more efficient call center workflows.",
    solution: "An AI-powered patient assistant that handles FAQs, doctor discovery, appointment booking, and multilingual referral support.",
    process: ["Patient chat and voice interactions", "Hospital knowledge and service mapping", "Appointment and routing workflows", "Cross-channel escalation to staff"],
    outcome: "Lower wait times and better patient access without adding operational burden.",
    accent: "healthcare",
  },
  {
    name: "24Streets AI Radiologist Assistant",
    problem: "Radiology teams are inundated with studies and need decision support without losing clinical accountability.",
    solution: "A vision-assisted AI review layer that highlights potential abnormalities and organizes image findings for review by qualified specialists.",
    process: ["Medical image analysis", "Abnormality prioritization", "Structured AI-assisted observations", "Clinician-led review and signoff"],
    outcome: "Faster triage and more consistent clinical workflows with AI acting as a support tool.",
    accent: "vision",
  },
  {
    name: "24Streets AI Doctor Assistant",
    problem: "Clinicians need a faster way to synthesize patient history and improve decision support without replacing medical judgment.",
    solution: "A doctor-side assistant that summarizes patient records, organizes diagnosis information, and supports evidence-based clinical workflows.",
    process: ["Patient history aggregation", "Clinical summary generation", "Treatment context support", "Prescription and workflow checks"],
    outcome: "Smarter, faster clinical preparation that keeps the doctor in control of decisions.",
    accent: "clinical",
  },
  {
    name: "Enterprise AI Agents",
    problem: "Most enterprises have fragmented tools and knowledge scattered across teams, systems, and documents.",
    solution: "AI agents that understand context, retrieve information, call APIs, and automate high-value workflows across internal systems.",
    process: ["Knowledge retrieval and RAG", "Enterprise API orchestration", "Workflow execution and handoffs", "Human-in-the-loop approvals"],
    outcome: "Connected operations with faster decisions, lower manual effort, and more scalable support.",
    accent: "enterprise",
  },
];

const visionIndustries = ["Healthcare", "Security", "Industrial", "Retail", "Infrastructure"];
const industries = ["Healthcare", "Financial Services", "Retail", "Manufacturing", "Defense & Security", "Real Estate", "Enterprise Services"];
const labAreas = ["Multimodal AI", "Vision AI", "Voice AI", "AI Agents", "RAG", "Small Language Models", "Predictive AI"];
const buildPath = ["Your Data", "AI Models", "RAG / Knowledge", "AI Agents", "Enterprise Systems", "Users"];
const processSteps = ["Discover", "Prototype", "Pilot", "Integrate", "Deploy", "Improve"];
const softwareStack = ["Web Applications", "Mobile Applications", "Cloud Systems", "APIs", "System Integration", "DevOps", "Data Platforms"];

function App() {
  const [typedText, setTypedText] = useState("");

  useEffect(() => {
    const revealItems = document.querySelectorAll(".reveal, .reveal-card");
    if (!revealItems.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    revealItems.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let phraseIndex = 0;
    let charIndex = 0;
    let deleting = false;
    const timeoutIds = [];

    const schedule = (callback, delay) => {
      const id = setTimeout(callback, delay);
      timeoutIds.push(id);
    };

    const tick = () => {
      const currentPhrase = phrases[phraseIndex];

      if (!deleting) {
        charIndex += 1;
        setTypedText(currentPhrase.slice(0, charIndex));

        if (charIndex === currentPhrase.length) {
          deleting = true;
          schedule(tick, 1200);
          return;
        }
      } else {
        charIndex -= 1;
        setTypedText(currentPhrase.slice(0, charIndex));

        if (charIndex === 0) {
          deleting = false;
          phraseIndex = (phraseIndex + 1) % phrases.length;
        }
      }

      schedule(tick, deleting ? 42 : 90);
    };

    schedule(tick, 500);

    return () => timeoutIds.forEach((id) => clearTimeout(id));
  }, []);

  return (
    <div className="site-shell">
      <header className="topbar reveal" id="top">
        <div className="brand" aria-label="24Streets home">
          <span className="brand-mark">24</span>
          <span className="brand-word">Streets</span>
        </div>

        <nav className="main-nav" aria-label="Main navigation">
          {navItems.map((item) => (
            <a key={item.href} href={item.href}>{item.label}</a>
          ))}
        </nav>

        <div className="nav-actions">
          <a href="#contact" className="btn btn-secondary">Book a Demo</a>
        </div>
      </header>

      <main>
        <section className="hero section-dark reveal" id="home">
          <div className="container hero-grid">
            <div className="hero-copy">
              <p className="eyebrow">AI engineering for real-world operations</p>
              <h1>
                We Build
                <span className="headline-stack">
                  <span className="typed-text">{typedText}</span>
                  <span className="cursor">|</span>
                </span>
                <br />
                That Works in the Real World.
              </h1>
              <p className="lead">
                24Streets designs and builds production-ready AI agents, computer vision systems,
                voice AI, and intelligent enterprise software that transform how organizations operate.
              </p>

              <div className="cta-row">
                <a href="#ai" className="btn btn-primary">Explore Our AI</a>
                <a href="#contact" className="btn btn-secondary">Talk to Our AI Team</a>
              </div>

              <div className="stats-row" aria-label="Company impact metrics">
                <div>
                  <strong>AI Products</strong>
                  <span>Real systems</span>
                </div>
                <div>
                  <strong>Healthcare</strong>
                  <span>Focused AI</span>
                </div>
                <div>
                  <strong>Enterprise</strong>
                  <span>Built to deploy</span>
                </div>
              </div>
            </div>

            <div className="hero-visual" aria-hidden="true">
              <div className="visual-panel">
                <div className="panel-glow" />
                <div className="flow-node data">Data</div>
                <div className="flow-node ai">AI</div>
                <div className="flow-node reasoning">Reasoning</div>
                <div className="flow-node action">Action</div>
                <div className="flow-line line-one" />
                <div className="flow-line line-two" />
                <div className="flow-line line-three" />
              </div>
            </div>
          </div>
        </section>

        <section className="section section-light reveal" id="ai">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow eyebrow-dark">Capabilities</p>
              <h2>From AI Models to Real Business Systems.</h2>
            </div>

            <div className="card-grid capability-grid">
              {capabilityCards.map((item) => (
                <article className="info-card reveal-card" key={item.title}>
                  <div className="mini-visual">
                    <span className="pulse-dot" />
                    <span className="pulse-line" />
                    <span className="pulse-line pulse-line-two" />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-dark reveal" id="solutions">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow">AI We’re Building</p>
              <h2>Production AI products designed for high-stakes environments.</h2>
            </div>

            <div className="product-list">
              {products.map((product) => (
                <article className={`product-card reveal-card ${product.accent}`} key={product.name}>
                  <div className="product-copy">
                    <p className="product-tag">AI Product</p>
                    <h3>{product.name}</h3>
                    <div className="detail-block">
                      <span>Problem</span>
                      <p>{product.problem}</p>
                    </div>
                    <div className="detail-block">
                      <span>AI solution</span>
                      <p>{product.solution}</p>
                    </div>
                    <div className="detail-block">
                      <span>How it works</span>
                      <ul>
                        {product.process.map((step) => (
                          <li key={step}>{step}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="detail-block">
                      <span>Business outcome</span>
                      <p>{product.outcome}</p>
                    </div>
                  </div>

                  <div className="product-visual" aria-hidden="true">
                    <div className="mock-window">
                      <div className="mock-header">
                        <span />
                        <span />
                        <span />
                      </div>
                      <div className="mock-body">
                        <div className="mock-panel panel-one" />
                        <div className="mock-panel panel-two" />
                        <div className="mock-panel panel-three" />
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-light vision-section reveal" id="vision">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow eyebrow-dark">Vision AI</p>
              <h2>AI That Can See.</h2>
              <p className="subtext">24Streets Vision AI transforms images and video into intelligence.</p>
            </div>

            <div className="vision-flow">
              <div className="vision-stage">Image / Video</div>
              <span className="arrow">→</span>
              <div className="vision-stage">Object Detection</div>
              <span className="arrow">→</span>
              <div className="vision-stage">Vision Model</div>
              <span className="arrow">→</span>
              <div className="vision-stage">Insights</div>
              <span className="arrow">→</span>
              <div className="vision-stage">Action</div>
            </div>

            <div className="industry-pills">
              {visionIndustries.map((industry) => (
                <span key={industry}>{industry}</span>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-dark agent-section reveal" id="agents">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow">AI Agents</p>
              <h2>AI That Doesn’t Just Answer. It Acts.</h2>
            </div>

            <p className="agent-copy">
              24Streets agents understand context, reason over enterprise information, retrieve the right data,
              call APIs, and execute workflows with human oversight when needed.
            </p>

            <div className="agent-flow" aria-label="AI agent architecture">
              <span>User</span>
              <span className="connect">↓</span>
              <span>AI Agent</span>
              <span className="connect">↓</span>
              <span>Knowledge / RAG</span>
              <span className="connect">↓</span>
              <span>Enterprise APIs</span>
              <span className="connect">↓</span>
              <span>Actions</span>
            </div>
          </div>
        </section>

        <section className="section section-light voice-section reveal" id="voice">
          <div className="container voice-grid">
            <div>
              <p className="eyebrow eyebrow-dark">Voice AI</p>
              <h2>AI That Can Listen and Speak.</h2>
              <p className="subtext">
                Voice AI systems built for hospitals, customer support, booking services, and enterprise operations.
              </p>

              <div className="voice-pipeline">
                <span>Speech</span>
                <span className="arrow-small">↓</span>
                <span>Speech-to-Text</span>
                <span className="arrow-small">↓</span>
                <span>AI Reasoning / RAG</span>
                <span className="arrow-small">↓</span>
                <span>Enterprise Systems</span>
                <span className="arrow-small">↓</span>
                <span>Text-to-Speech</span>
              </div>
            </div>

            <div className="audio-visual" aria-hidden="true">
              <div className="wave wave-one" />
              <div className="wave wave-two" />
              <div className="wave wave-three" />
              <div className="wave wave-four" />
              <div className="wave wave-five" />
            </div>
          </div>
        </section>

        <section className="section section-dark healthcare-section reveal" id="healthcare">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow">Healthcare AI</p>
              <h2>Building the Intelligent Hospital.</h2>
            </div>

            <div className="hospital-ecosystem">
              <div className="health-node">Patient</div>
              <div className="health-node">Doctor</div>
              <div className="health-node">Radiologist</div>
              <div className="health-node">Hospital Administration</div>
              <div className="health-node">Call Center</div>
              <div className="health-core">24Streets Healthcare AI Platform</div>
            </div>

            <div className="health-grid">
              <div>
                <h3>AI products</h3>
                <ul>
                  <li>Hospital AI Assistant</li>
                  <li>Doctor AI Assistant</li>
                  <li>Radiologist AI Assistant</li>
                  <li>Voice AI</li>
                  <li>Patient Knowledge Platform</li>
                </ul>
              </div>
              <div>
                <h3>Important principle</h3>
                <p>
                  AI assists clinicians and hospital teams. It does not replace qualified doctors or radiologists;
                  it supports decision-making, workflow efficiency, and patient experience.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section section-light reveal" id="about">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow eyebrow-dark">How We Build AI</p>
              <h2>From data, models, and intelligence to enterprise deployment.</h2>
            </div>

            <div className="build-flow" aria-label="AI system architecture">
              {buildPath.map((step, index) => (
                <React.Fragment key={step}>
                  <div className="build-step">{step}</div>
                  {index < buildPath.length - 1 && <span className="arrow">↓</span>}
                </React.Fragment>
              ))}
            </div>

            <div className="stack-row">
              <span>LLMs</span>
              <span>Computer Vision</span>
              <span>Speech AI</span>
              <span>Vector Databases</span>
              <span>RAG</span>
              <span>Machine Learning</span>
              <span>APIs</span>
              <span>Cloud Infrastructure</span>
            </div>
          </div>
        </section>

        <section className="section section-dark process-section reveal">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow">AI Development Process</p>
              <h2>Prototype quickly. Productionize with discipline.</h2>
            </div>

            <div className="process-flow" aria-label="AI development process">
              {processSteps.map((step, index) => (
                <React.Fragment key={step}>
                  <div className="process-step">{step}</div>
                  {index < processSteps.length - 1 && <span className="arrow">↓</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-light reveal" id="industries">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow eyebrow-dark">Industries</p>
              <h2>Solutions we have built and industries we support.</h2>
            </div>

            <div className="industry-grid">
              {industries.map((industry) => (
                <div className="industry-item" key={industry}>{industry}</div>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-dark lab-section reveal" id="lab">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow">24Streets AI Lab</p>
              <h2>We continuously turn emerging AI into practical business products.</h2>
            </div>

            <div className="lab-grid">
              {labAreas.map((area) => (
                <div className="lab-item" key={area}>{area}</div>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-light engineering-section reveal">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow eyebrow-dark">Software Engineering</p>
              <h2>AI needs great software engineering behind it.</h2>
            </div>

            <p className="subtext">
              We build the software foundation around AI systems: web apps, mobile products, APIs, integrations,
              data platforms, and operational infrastructure that keep production deployments reliable.
            </p>

            <div className="engineering-grid">
              {softwareStack.map((item) => (
                <div className="engineering-item" key={item}>{item}</div>
              ))}
            </div>
          </div>
        </section>

        <section className="final-cta reveal" id="contact">
          <div className="container cta-panel">
            <div>
              <p className="eyebrow">Let’s build what’s next</p>
              <h2>Have a problem AI could solve?</h2>
            </div>
            <p>
              Talk to our AI engineering team and explore what we can build together.
            </p>
            <div className="cta-row">
              <a href="mailto:services@24streets.com" className="btn btn-primary">Start an AI Project</a>
              <a href="mailto:services@24streets.com?subject=Book%20a%20Demo" className="btn btn-secondary">Book a Demo</a>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container footer-grid">
          <div>
            <h3>24Streets</h3>
            <p>AI engineering, healthcare AI, computer vision, enterprise automation, and custom software for real businesses.</p>
          </div>
          <div>
            <h4>Contact</h4>
            <a href="mailto:services@24streets.com">services@24streets.com</a>
            <a href="tel:+94775822197">+94 775 822 197</a>
            <a href="tel:+94770373556">+94 770 373 556</a>
          </div>
          <div>
            <h4>Address</h4>
            <p>No 918, Electricity Board Rd, Battaramulla, Sri Lanka</p>
          </div>
        </div>
        <div className="container footer-bottom">
          <span>© {new Date().getFullYear()} 24Streets. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
